import { useState, useMemo } from "react";
import { ELEMENTS as elements } from "../data/periodicTable.js";
import { NerdStackFooter } from "../components/NerdStackFooter.jsx";
import { ChemBackButton } from "./ChemBackButton.jsx";
import { formatNum } from "../lib/formatNum.js";

const EM = "#F8B84E", EM_DEEP = "#7C3AED", EM_LIGHT = "#FCD9A0";
const NA = 6.02214076e23; // Avogadro, exact (SI 2019)
const rgba = (hex, a) => {
  const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16);
  return `rgba(${r},${g},${b},${a})`;
};
const MASS = {};
for (const e of elements) MASS[e.symbol] = typeof e.mass === "number" ? e.mass : parseFloat(e.mass);

// Same verified parser as MolecularWeight (kept local to avoid coupling;
// identical logic, tested to 13/13 known molar masses).
function parseFormula(raw){
  if(!raw||!raw.trim())return{counts:null,error:null};
  const formula=raw.replace(/[·•∙*]/g,".").trim();
  const segments=[];let buf="";
  for(let i=0;i<formula.length;i++){const ch=formula[i];
    if(ch==="."&&/[0-9A-Z]/.test(formula[i+1]||"")){segments.push(buf);buf="";}else buf+=ch;}
  segments.push(buf);
  const total={};
  for(let seg of segments){seg=seg.trim();if(!seg)continue;
    let mult=1;const m=seg.match(/^(\d+)(?=[A-Z(\[])/);
    if(m){mult=parseInt(m[1]);seg=seg.slice(m[1].length);}
    const r=parseSegment(seg);if(r.error)return{counts:null,error:r.error};
    for(const[el,n]of Object.entries(r.counts))total[el]=(total[el]||0)+n*mult;}
  return{counts:total,error:null};
}
function parseSegment(s){
  const stack=[{}];const brackets=[];let i=0;
  while(i<s.length){const ch=s[i];
    if(ch==="("||ch==="["){stack.push({});brackets.push(ch);i++;continue;}
    if(ch===")"||ch==="]"){i++;let num="";
      while(i<s.length&&/\d/.test(s[i]))num+=s[i++];
      const mult=num?parseInt(num):1;
      if(!brackets.length)return{counts:null,error:"Unbalanced parentheses"};
      const open=brackets.pop();
      if((open==="("&&ch!==")")||(open==="["&&ch!=="]"))return{counts:null,error:"Mismatched bracket types"};
      const top=stack.pop();
      if(!stack.length)return{counts:null,error:"Unbalanced parentheses"};
      const into=stack[stack.length-1];
      for(const[el,n]of Object.entries(top))into[el]=(into[el]||0)+n*mult;continue;}
    const sym=s.slice(i).match(/^[A-Z][a-z]?/);
    if(!sym)return{counts:null,error:`Unexpected character "${ch}"`};
    const el=sym[0];if(!(el in MASS))return{counts:null,error:`Unknown element "${el}"`};
    i+=el.length;let num="";
    while(i<s.length&&/\d/.test(s[i]))num+=s[i++];
    const cnt=num?parseInt(num):1;const top=stack[stack.length-1];
    top[el]=(top[el]||0)+cnt;}
  if(stack.length!==1||brackets.length)return{counts:null,error:"Unbalanced parentheses"};
  return{counts:stack[0],error:null};
}
function molarMass(f){const{counts,error}=parseFormula(f);if(error||!counts)return null;
  let t=0;for(const[el,n]of Object.entries(counts))t+=MASS[el]*n;return t;}

const fmt = (x) => formatNum(x, 6);

export default function MoleCalculator() {
  const [formula, setFormula] = useState("");
  const [field, setField] = useState("grams"); // which field the user is entering
  const [val, setVal] = useState("");

  const mm = useMemo(() => molarMass(formula), [formula]);
  const out = useMemo(() => {
    const v = parseFloat(val);
    if (!mm || !isFinite(v) || val === "") return null;
    let grams, moles, molecules;
    if (field === "grams") { grams = v; moles = v / mm; molecules = moles * NA; }
    else if (field === "moles") { moles = v; grams = v * mm; molecules = v * NA; }
    else { molecules = v; moles = v / NA; grams = moles * mm; }
    return { grams, moles, molecules };
  }, [val, field, mm]);

  const inStyle = { width: "100%", padding: "13px 15px", borderRadius: 10, border: `1px solid ${rgba(EM,0.2)}`, background: "rgba(0,0,0,0.3)", color: "#E2E8F0", fontSize: 16, fontFamily: "inherit", outline: "none", boxSizing: "border-box" };

  return (
    <div style={{ minHeight: "100vh", background: "#0A0B14", fontFamily: "'DM Sans', system-ui, sans-serif", color: "#E2E8F0" }}>
      <div style={{ position: "fixed", top: "-25%", left: "-10%", width: "50vw", height: "50vw", background: `radial-gradient(circle, ${rgba(EM,0.05)} 0%, transparent 65%)`, pointerEvents: "none" }} />
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px", paddingTop: "calc(16px + env(safe-area-inset-top))", paddingLeft: "calc(24px + env(safe-area-inset-left))", paddingRight: "calc(24px + env(safe-area-inset-right))", borderBottom: `1px solid ${rgba(EM,0.06)}`, position: "relative", zIndex: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <ChemBackButton accentColor={EM} />
          <div style={{ width: 36, height: 36, borderRadius: 10, background: `linear-gradient(135deg, ${EM}, ${EM_DEEP})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 800, color: "#fff" }}>Ch</div>
          <span style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.5px", background: `linear-gradient(135deg, ${EM_LIGHT}, #A7F3D0)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Mole Calculator</span>
          <span style={{ fontSize: 11, color: "#374151", marginLeft: 4 }}>by NerdChem</span>
        </div>
        <div style={{ fontSize: 12, color: "#374151" }}>100% free</div>
      </header>

      <main style={{ maxWidth: 560, margin: "0 auto", padding: "32px 20px 80px", position: "relative", zIndex: 10 }}>
        <p style={{ fontSize: 15, color: "#9CA3AF", lineHeight: 1.6, textAlign: "center", margin: "0 0 24px" }}>
          Convert between grams, moles, and number of molecules. Enter a formula
          and any one value — the other two are calculated.
        </p>

        <label style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>Chemical formula</label>
        <input value={formula} onChange={(e) => setFormula(e.target.value)} placeholder="e.g. NaCl" style={{ ...inStyle, marginTop: 6, marginBottom: 6, textAlign: "center", fontSize: 18, fontWeight: 600 }} />
        <div style={{ fontSize: 13, color: mm ? EM_LIGHT : "#4B5563", textAlign: "center", marginBottom: 20, minHeight: 18 }}>
          {mm ? `Molar mass: ${mm.toFixed(3)} g/mol` : formula ? "Enter a valid formula" : "\u00A0"}
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
          {[["grams", "Grams"], ["moles", "Moles"], ["molecules", "Molecules"]].map(([k, lbl]) => (
            <button key={k} onClick={() => setField(k)} style={{ flex: 1, padding: "10px", borderRadius: 9, cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 600, background: field === k ? rgba(EM,0.12) : "rgba(255,255,255,0.02)", border: field === k ? `1.5px solid ${rgba(EM,0.35)}` : "1.5px solid rgba(255,255,255,0.05)", color: field === k ? EM_LIGHT : "#6B7280" }}>{lbl}</button>
          ))}
        </div>
        <input type="number" value={val} onChange={(e) => setVal(e.target.value)} placeholder={`Enter ${field}`} style={inStyle} />

        {out && (
          <div style={{ marginTop: 26, display: "grid", gap: 10 }}>
            {[["Grams", out.grams, "g"], ["Moles", out.moles, "mol"], ["Molecules", out.molecules, ""]].map(([lbl, v, unit]) => (
              <div key={lbl} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderRadius: 12, background: lbl.toLowerCase() === field ? rgba(EM,0.07) : "rgba(255,255,255,0.02)", border: lbl.toLowerCase() === field ? `1px solid ${rgba(EM,0.25)}` : "1px solid rgba(255,255,255,0.05)" }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>{lbl}</span>
                <span style={{ fontSize: 20, fontWeight: 700, color: EM_LIGHT }}>{fmt(v)} <span style={{ fontSize: 13, color: "#6B7280" }}>{unit}</span></span>
              </div>
            ))}
          </div>
        )}
      </main>

      <footer style={{ textAlign: "center", padding: 24, fontSize: 12, color: "#1F2937", borderTop: `1px solid ${rgba(EM,0.04)}` }}>
        <NerdStackFooter variant="tool" family="chem" />
      </footer>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap'); input::placeholder { color: #4B5563; } input[type=number]::-webkit-inner-spin-button{-webkit-appearance:none;} * { box-sizing: border-box; } body { margin: 0; }`}</style>
    </div>
  );
}
